import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { LayoutDashboard, ShieldCheck, LogOut, Menu, X, Gauge, UserCog, Wrench, FileText } from "lucide-react";
import { ChangeEmailDialog } from "@/components/ChangeEmailDialog";
import { roleLabel } from "@/lib/roles";

export function AppLayout({ children }) {
  const { user, logout, isAdmin, isTech } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const nav = isTech
    ? [{ to: "/technician", label: "Technician Assist", icon: Wrench, testid: "nav-technician" }]
    : [
        { to: "/", label: "My Compliance", icon: LayoutDashboard, testid: "nav-dashboard" },
        { to: "/technician", label: "Technician Assist", icon: Wrench, testid: "nav-technician" },
        ...(isAdmin ? [{ to: "/admin", label: "Admin Console", icon: ShieldCheck, testid: "nav-admin" }, { to: "/hyundai", label: "Hyundai Claim Portal", icon: FileText, testid: "nav-hyundai" }, { to: "/gm", label: "GM Claim Portal", icon: FileText, testid: "nav-gm" }] : []),
      ];

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-border px-6 py-5">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary shadow-lg shadow-primary/30">
          <Gauge className="h-5 w-5 text-white" />
        </div>
        <div>
          <p className="font-heading text-lg font-extrabold uppercase leading-none tracking-tight">Warranty</p>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Compliance Pro</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-5">
        {nav.map((item) => {
          const active = location.pathname === item.to;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={cn(
                "group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150",
                active
                  ? "bg-primary/15 text-primary shadow-inner"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              <item.icon className={cn("h-4 w-4", active ? "text-primary" : "")} />
              {item.label}
              {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />}
            </Link>
          );
        })}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg bg-accent/40 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-blue-500 text-xs font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold">{user?.name}</p>
            <Badge
              variant="outline"
              className={cn(
                "mt-0.5 h-4 px-1.5 font-mono text-[9px] uppercase tracking-wider",
                isAdmin ? "border-violet-500/40 bg-violet-500/15 text-violet-300" : "border-blue-500/40 bg-blue-500/15 text-blue-300"
              )}
            >
              {isAdmin ? <UserCog className="mr-1 h-2.5 w-2.5" /> : null}
              {roleLabel(user?.role)}
            </Badge>
          </div>
        </div>
        <ChangeEmailDialog />
        <Button
          variant="ghost"
          data-testid="logout-button"
          onClick={handleLogout}
          className="mt-2 w-full justify-start gap-2 text-muted-foreground hover:text-red-300"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[260px] border-r border-border bg-card/60 backdrop-blur-md lg:block">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[260px] border-r border-border bg-card">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="lg:pl-[260px]">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-border glass px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
            {isAdmin ? "Administrator Console" : (user?.dealership?.name || "Vehicle Repair Order Compliance")}
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!isAdmin && user?.dealership?.has_logo && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/dealerships/${user.tenant_id}/logo`} alt="dealership logo" className="h-7 rounded bg-white/90 px-1" />
            )}
            <span className="hidden font-mono text-xs text-muted-foreground sm:block">{user?.email}</span>
          </div>
        </header>
        <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
